import { put, takeLatest, select } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import UserService from '../../../services/User'
import RESTModelNormalizationSchemas from '../../models/RESTModelNormalizationSchemas'
import StoreUser from '../../models/User'
import DataStoresActions from '../../actions/data'
import ListMetaData from '../../../modules/utilities/list/ListMetaData'
import FilterExpression from '../../../modules/utilities/list/FilterExpression'
import UserDataStoreSlice from '../../slices/data/UserDataStoreSlice'
import ErrorActions from '../../actions/Error'
import OLError from '../../../modules/errorHandling/models/OLError'
import UserManagementExperienceActions from '../../actions/experiences/UserManagement'
import USER_MANAGEMENT_EXPERIENCE_TYPES from '../../types/experiences/UserManagement'
import LayoutActions from '../../actions/Layout'
import UserManagementUserListSlice from '../../slices/experiences/UserManagementUserListSlice'
import MessageActions from '../../actions/Messages'
import { AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT, DISPLAYED_MESSAGE_TYPES } from '../../../constants/general'
import UserManagementUserListExportSlice from '../../slices/experiences/UserManagementUserListExportSlice'
import UserManagementUserActionListSlice from '../../slices/experiences/UserManagementUserActionListSlice'
import UserManagementUserActionListExportSlice from '../../slices/experiences/UserManagementUserActionListExportSlice'
import UserActionService from '../../../services/UserAction'
import UserActionDataStoreSlice from '../../slices/data/UserActionDataStoreSlice'
import { IS_DEV_ENV } from '../../../constants/general'

/** Layout fetch ids — use with storeHooks.useLoading(id) for scoped button loading. */
export const USER_MANAGEMENT_FETCH_IDS = {
  CREATE: 'UserManagementExperienceSaga.createUser',
  EDIT: 'UserManagementExperienceSaga.editUser',
  PATCH_POLICY: 'UserManagementExperienceSaga.patchUserPolicy',
  PATCH_PHOTO: 'UserManagementExperienceSaga.patchUserPhoto',
  CHANGE_PASSWORD: 'UserManagementExperienceSaga.changeUserPassword',
  DELETE: 'UserManagementExperienceSaga.deleteUser',
  UPDATE_ATTRIBUTE_ACTIVE: 'UserManagementExperienceSaga.updateUserAttributeActive'
}

function * fetchUsers (action) {
  const sliceActions = action.type === UserManagementUserListExportSlice.types.FETCH
    ? UserManagementExperienceActions.exportList
    : UserManagementExperienceActions.list
  try {
    const { data: { limit, page, sort, filters = [], filtersLinkingType } } = action
    yield put(sliceActions.setIsLoading(true))

    const filterArgs = Array.isArray(filters) ? filters : []
    const filter = filterArgs.length > 0
      ? new FilterExpression(filtersLinkingType, ...filterArgs)
      : null

    const result = yield UserService.FindMany(
      new ListMetaData(limit, page, sort, filter)
    )
    yield put(
      UserDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          yield put(
            sliceActions.setData(storeValues.result, storeValues.totalNrOfItems, false)
          )
          yield put(sliceActions.setIsLoading(false))
        }
      )
    )
  } catch (e) {
    yield put(sliceActions.setIsLoading(false))
    const details = {
      message: e?.message,
      ...(e?.response && {
        status: e.response.status,
        statusText: e.response.statusText,
        data: e.response.data
      })
    }
    yield put(ErrorActions.trigger(
      new OLError(
        '[UserManagementExperience->fetchUsers]: Failed to fetch users',
        'Failed to fetch users',
        details
      ),
      true,
      true,
      true,
      false
    ))
  }
}

function * createUser (action) {
  const fetchId = USER_MANAGEMENT_FETCH_IDS.CREATE
  let onSuccess
  let succeeded = false
  let createdUserId
  yield put(LayoutActions.setIsLoading(true, fetchId))
  try {
    const { data: { userItem, onSuccess: successCb, photoId } } = action
    onSuccess = successCb

    const result = yield UserService.Create(userItem.toNormalizedRESTModel())
    if (result.status === 200) {
      createdUserId = result.data?.id
      if (photoId && createdUserId) {
        const photoResult = yield UserService.Update(createdUserId, { photoId })
        if (photoResult.status === 200) {
          yield mergeUserFromUpdateResponse(createdUserId, photoResult)
        }
      }
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['CREATE_USER_SUCCESS', 'User successfully created'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      succeeded = true
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[UserManagementExperience->createUser]: Failed to create user', 'Failed to create user', e),
        true,
        true,
        true,
        false
      )
    )
  } finally {
    yield put(LayoutActions.setIsLoading(false, fetchId))
  }
  if (succeeded && typeof onSuccess === 'function') {
    onSuccess(createdUserId)
  }
}

function * mergeUserFromUpdateResponse (userId, result, extraFields = {}) {
  try {
    const normalizedData = normalize(result.data, RESTModelNormalizationSchemas.user.schema)
    const updatedUser = StoreUser.FromNormalizedRESTModel(
      normalizedData.entities[RESTModelNormalizationSchemas.user.entityName][normalizedData.result]
    )
    yield put(
      DataStoresActions.mergeEntitiesIntoStores({
        [RESTModelNormalizationSchemas.user.entityName]: {
          [String(userId)]: {
            ...updatedUser.toJSON(),
            ...extraFields
          }
        }
      })
    )
  } catch (e) {
    if (IS_DEV_ENV) {
      console.error('[UserManagementExperience] Failed to merge user update into store:', e)
    }
  }
}

function * editUser (action) {
  const fetchId = USER_MANAGEMENT_FETCH_IDS.EDIT
  let onSuccess
  let succeeded = false
  yield put(LayoutActions.setIsLoading(true, fetchId))
  try {
    const { data: { userItem, onSuccess: successCb } } = action
    onSuccess = successCb

    const result = yield UserService.Update(userItem.id, userItem.toNormalizedRESTModel())
    if (result.status === 200) {
      yield mergeUserFromUpdateResponse(userItem.id, result)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_USER_SUCCESS', 'User successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      succeeded = true
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[UserManagementExperience->editUser]: Failed to update user', 'Failed to update user', e),
        true,
        true,
        true,
        false
      )
    )
  } finally {
    yield put(LayoutActions.setIsLoading(false, fetchId))
  }
  if (succeeded && typeof onSuccess === 'function') {
    onSuccess()
  }
}

function * patchUserPhoto (action) {
  const { data: { userId, photoId } } = action

  try {
    const result = yield UserService.Update(userId, { photoId })
    if (result.status === 200) {
      yield mergeUserFromUpdateResponse(userId, result)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_USER_PHOTO_SUCCESS', 'Profile photo updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[UserManagementExperience->patchUserPhoto]: Failed to update profile photo',
          'Failed to update profile photo',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
}

function * patchUserPolicy (action) {
  const { data: { userId, patch } } = action

  try {
    const result = yield UserService.Update(userId, patch)
    if (result.status === 200) {
      yield mergeUserFromUpdateResponse(userId, result)
      const messageKey = patch.areFormFillsMandatory !== undefined
        ? ['EDIT_USER_FORMS_REQUIRED_SUCCESS', 'Forms requirement updated']
        : ['EDIT_USER_2FA_SUCCESS', 'Two-factor authentication setting updated']
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          messageKey,
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[UserManagementExperience->patchUserPolicy]: Failed to update user policy',
          'Failed to update user policy',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
}

function * changeUserPassword (action) {
  const fetchId = USER_MANAGEMENT_FETCH_IDS.CHANGE_PASSWORD
  let onSuccess
  let succeeded = false
  yield put(LayoutActions.setIsLoading(true, fetchId))
  try {
    const { data: { userId, password, onSuccess: successCb } } = action
    onSuccess = successCb

    const result = yield UserService.ChangePassword(userId, password)
    if (result.status === 200) {
      yield mergeUserFromUpdateResponse(userId, result, { hasPassword: true })
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['CHANGE_USER_PASSWORD_SUCCESS', 'Password successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      succeeded = true
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[UserManagementExperience->changeUserPassword]: Failed to change password', 'Failed to change password', e),
        true,
        true,
        true,
        false
      )
    )
  } finally {
    yield put(LayoutActions.setIsLoading(false, fetchId))
  }
  if (succeeded && typeof onSuccess === 'function') {
    onSuccess()
  }
}

function * updateUserAttributeActive (action) {
  const fetchId = USER_MANAGEMENT_FETCH_IDS.UPDATE_ATTRIBUTE_ACTIVE
  yield put(LayoutActions.setIsLoading(true, fetchId))
  try {
    const { data: { userId, isActive } } = action

    const result = yield UserService.Update(userId, { isActive })
    if (result.status === 200) {
      yield mergeUserFromUpdateResponse(userId, result)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['USER_ACTIVE_ATTRIBUTE_UPDATE_SUCCESS', 'User active state was successfully changed'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[UserManagementExperience->updateUserAttributeActive]: Changing the active state has failed',
          'Failed to change the user\'s active state',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  } finally {
    yield put(LayoutActions.setIsLoading(false, fetchId))
  }
}

function * deleteUser (action) {
  const fetchId = USER_MANAGEMENT_FETCH_IDS.DELETE
  yield put(LayoutActions.setIsLoading(true, fetchId))
  try {
    const { data: { userId } } = action

    const result = yield UserService.Delete(userId)
    const ok = result && result.status >= 200 && result.status < 300
    if (ok) {
      // Remove the id from the list slice before deleting from the normalized store, so hooks
      // (useDataStoreItems) never see "id in list but missing in store" and refetch the user.
      const listState = yield select(state => state.experiences.userManagement.list)
      const currentIds = Array.isArray(listState?.data) ? listState.data : []
      const uid = String(userId)
      const nextIds = currentIds.filter(id => String(id) !== uid)
      if (nextIds.length !== currentIds.length) {
        const total = Math.max(0, (listState?.totalNumberOfItems || 0) - 1)
        yield put(UserManagementExperienceActions.list.setData(nextIds, total, false))
      }
      yield put(UserDataStoreSlice.actions.deleteItem(userId))
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['DELETE_USER_SUCCESS', 'User deleted (soft). Login access was revoked.'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[UserManagementExperience->deleteUser]: Failed to delete user', 'Failed to delete user', e),
        true,
        true,
        true,
        false
      )
    )
  } finally {
    yield put(LayoutActions.setIsLoading(false, fetchId))
  }
}

function * fetchUserActions (action) {
  const sliceActions = action.type === UserManagementUserActionListExportSlice.types.FETCH
    ? UserManagementExperienceActions.exportActionList
    : UserManagementExperienceActions.actionList
  try {
    const { data: { limit, page, sort, filters = [], filtersLinkingType } } = action
    yield put(sliceActions.setIsLoading(true))

    const filterArgs = Array.isArray(filters) ? filters : []
    const filter = filterArgs.length > 0
      ? new FilterExpression(filtersLinkingType, ...filterArgs)
      : null

    const result = yield UserActionService.FindMany(
      new ListMetaData(limit, page, sort, filter)
    )
    yield put(
      UserActionDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          yield put(
            sliceActions.setData(storeValues.result, storeValues.totalNrOfItems, false)
          )
        }
      )
    )
  } catch (e) {
    yield put(sliceActions.setIsLoading(false))
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[UserManagementExperience->fetchUserActions]: Failed to fetch user actions',
          'Failed to fetch user actions',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
}

function * UserManagementExperienceSaga () {
  yield takeLatest(UserManagementUserListSlice.types.FETCH, fetchUsers)
  yield takeLatest(UserManagementUserListExportSlice.types.FETCH, fetchUsers)
  yield takeLatest(USER_MANAGEMENT_EXPERIENCE_TYPES.CREATE, createUser)
  yield takeLatest(USER_MANAGEMENT_EXPERIENCE_TYPES.EDIT, editUser)
  yield takeLatest(USER_MANAGEMENT_EXPERIENCE_TYPES.CHANGE_PASSWORD, changeUserPassword)
  yield takeLatest(USER_MANAGEMENT_EXPERIENCE_TYPES.PATCH_POLICY, patchUserPolicy)
  yield takeLatest(USER_MANAGEMENT_EXPERIENCE_TYPES.PATCH_PHOTO, patchUserPhoto)
  yield takeLatest(USER_MANAGEMENT_EXPERIENCE_TYPES.UPDATE_ATTRIBUTE_ACTIVE, updateUserAttributeActive)
  yield takeLatest(USER_MANAGEMENT_EXPERIENCE_TYPES.DELETE, deleteUser)
  yield takeLatest(UserManagementUserActionListSlice.types.FETCH, fetchUserActions)
  yield takeLatest(UserManagementUserActionListExportSlice.types.FETCH, fetchUserActions)
}

export default UserManagementExperienceSaga
